<template>
  <div id="payment-cadence-modal">
    <Modal modalId="change-payment-cadence-modal" size="modal-lg modal-size" @hidden="closeChangeCadenceModal()">
      <div v-if="this.loadingData">
        <b-row class="text-center pt-5 pb-5">
          <b-col>
            <b-spinner style="width: 10rem; height: 10rem;" variant="warning" label="Spinning"></b-spinner>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <ModalHeader
          :title="$t('xManagePlan')"
          icon="far fa-circle-dollar colorOrange mr-1"
          :style="{ padding: '4px 12px' }"
        >
          <div class="cols-auto">
            <button
              type="button"
              class="btn btn-icon-button btn-icon-button--gray"
              @click="closeChangeCadenceModal()"
              data-test-hook="CloseModalBtn"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </ModalHeader>
        <div v-if="paying">
          <b-row class="text-center">
            <b-col>
              <b-spinner style="width: 10rem; height: 10rem;" variant="warning" label="Spinning"></b-spinner>
            </b-col>
          </b-row>
        </div>
        <div v-else>
          <ModalBody>
            <div class="row mb-3">
              <div class="col">
                <div data-test-hook="YouSaveMoneyText">
                  <div v-if="this.yearly">
                    {{ $t('xSaveMoneyYearlyText', { 0: this.currentSubPrice }) }}
                  </div>
                  <div v-else-if="this.monthly">
                    {{ $t('xSaveMoneyMonthlyText', { 0: this.getNewPlanPrice() }) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <span class="text" data-test-hook="RenewalDetails">
                  {{ $t('xRenewalDetails') }}
                  <ul>
                    <li>
                      {{
                        $t('xCurrentPlanDetailsText', {
                          0: $moment(this.purchasedDate).format('MM/DD/YYYY'),
                          1: $moment(endDate).format('MM/DD/YYYY')
                        })
                      }}
                    </li>
                    <!-- new plan starts on the same day the old plan ends -->
                    <li v-if="this.yearly">
                      {{
                        $t('xNewMonthlyPaymentText', {
                          0: this.getNewPlanPrice(),
                          1: $moment(endDate).format('MM/DD/YYYY')
                        })
                      }}
                    </li>
                    <li v-else-if="this.monthly">
                      {{
                        $t('xNewYearlyPaymentText', {
                          0: this.getNewPlanPrice(),
                          1: $moment(endDate).format('MM/DD/YYYY')
                        })
                      }}
                    </li>
                  </ul>
                </span>
              </div>
            </div>
            <div class="row" data-test-hook="AreYouSureYouWantToSwitchText">
              <div v-if="this.yearly" class="col">
                {{ $t('xAreYouSureMonthlyText') }}
              </div>
              <div v-else-if="this.monthly" class="col">
                {{ $t('xAreYouSureYearlyText') }}
              </div>
            </div>
          </ModalBody>
          <ModalFooter class="d-flex justify-content-center pr-0 pl-0">
            <div v-if="this.yearly" class="modal-buttons">
              <button
                type="button"
                class="btn btn-outline-warning"
                @click="closeChangeCadenceModal()"
                :title="$t('xKeepYearlyBtn')"
                data-test-hook="KeepCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xKeepYearlyBtn') }}
              </button>
              <button
                type="button"
                class="btn btn-warning ml-2"
                @click="changePaymentCadence()"
                :title="$t('xSwitchMonthlyBtn')"
                data-test-hook="SwitchCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xSwitchMonthlyBtn') }}
              </button>
            </div>
            <div v-else-if="this.monthly" class="modal-buttons">
              <button
                type="button"
                class="btn btn-outline-warning"
                @click="closeChangeCadenceModal()"
                :title="$t('xKeepMonthlyBtn')"
                data-test-hook="KeepCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xKeepMonthlyBtn') }}
              </button>
              <button
                type="button"
                class="btn btn-warning ml-2"
                @click="changePaymentCadence()"
                :title="$t('xSwitchYearlyBtn')"
                data-test-hook="SwitchCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xSwitchYearlyBtn') }}
              </button>
            </div>
          </ModalFooter>
        </div>
      </div>
    </Modal>
    <PaymentModal
      :modal-id="'change-cadence-payment'"
      :selected-product="selectedProduct"
      :account-profile="accountProfile"
      @submit="subscribeToNewProduct"
      @goBack="returnToManagePlanModal"
      @savePremiumFeaturesGettingReadyInfo="savePremiumFeaturesGettingReadyInfo"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Constants from '@/constants'
import PaymentModal from '@/components/Enrollment/Subscription/PaymentModal.vue'
import { isSubscriptionFromNonWebStore, isSubscriptionPendingChange } from '@/utils/subscriptions'
import { isDealerManaged, subscriptionDetailContainsApparatus, subscriptionDetailActive } from '@/utils/mixins'

export default {
  components: { PaymentModal },
  name: 'PaymentCadenceModal',
  data() {
    return {
      apparatuses: null,
      accountData: null,
      purchasedDate: null,
      currentSubPrice: null,
      yearly: false,
      monthly: false,
      subscription: null,
      selectedProduct: null,
      productsArray: null,
      currentSubProductId: null,
      existingPaymentInfo: null,
      loadingData: false,
      errorMessages: null,
      expirationMonth: null,
      expirationYear: null,
      paying: false
    }
  },
  props: ['endDate', 'oldSubscriptionProductId', 'subscriptionStore'],
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile
    })
  },
  methods: {
    async savePremiumFeaturesGettingReadyInfo() {
      var subscriptionDetails = {}
      await this.$store.dispatch('getSubscriptionDetails', this.accountProfile.organizationId).then(response => {
        subscriptionDetails = response.data.subscriptions.find(
          sub =>
            subscriptionDetailActive(sub.status) &&
            !isDealerManaged(sub.productId) &&
            sub.apparatuses?.length > 0 &&
            subscriptionDetailContainsApparatus(sub.apparatuses, this.subscription[0].apparatuses[0].apparatusId) //they have to upgrade all apparatus on old sub, we only need to look for one of the apparatuses
        )
      })
      // if processing is complete, show toast
      if (
        subscriptionDetails &&
        (subscriptionDetails?.product.productId === this.selectedProduct.productId ||
          subscriptionDetails?.status === Constants.SubscriptionDetailPendingProductChange)
      ) {
        this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xPlanChangedSuccessfully'),
          variant: 'success'
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        // processing not complete, add each apparatus data to state to trigger notification banner
        await this.apparatuses.forEach(apparatus => {
          this.$store.dispatch('addPremiumFeaturesGettingReadyInfo', {
            apparatusId: apparatus.apparatusId,
            productSubscribedTo: this.selectedProduct.productId,
            timeStamp: Date.now()
          })
        })
        window.location.reload()
      }
    },
    returnToManagePlanModal() {
      this.$bvModal.hide('change-cadence-payment')
      this.closeChangeCadenceModal()
      this.openModal('manage-plan-modal')
    },
    closeChangeCadenceModal() {
      this.closeModal('change-payment-cadence-modal')
    },
    async changePaymentCadence() {
      this.paying = true
      this.openModal('change-payment-cadence-modal')
      await this.$store.dispatch('getAccountProfile')
      this.closeChangeCadenceModal()
      this.$bvModal.show('change-cadence-payment')
      this.paying = false
    },
    async pollForSubscriptionDataAndRefresh() {
      this.loadingData = true
      this.openModal('change-payment-cadence-modal')
      // Check for apparatus data repeatedly or for 5 seconds
      const operationStart = Date.now()
      const delay = ms => new Promise(res => setTimeout(res, ms))
      let subscription = this.subscriptionDetails?.subscriptions?.filter(
        x => !x.isDealerManaged && x.status !== Constants.SubscriptionDetailPendingCancel
      )
      while (Date.now() - operationStart < 5000 && !subscription) {
        const response = await this.$store
          .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
          .catch(() => {})
        this.subscriptionDetails = response.data
        subscription = this.subscriptionDetails?.subscriptions?.filter(x => !x.isDealerManaged)
        await delay(1000)
      }
      if (!subscription) {
        this.closeModal('change-payment-cadence-modal')
        this.loadingData = false
        // Show processing toast
        this.$store.dispatch('displayToast', {
          title: this.$t('xProcessingSubscriptionInformation'),
          message: this.$t('xProcessingPlanChange'),
          variant: 'info'
        })
      } else {
        this.closeModal('change-payment-cadence-modal')
        this.loadingData = false
        // Reload the window to show the new UI
        window.location.reload()
      }
    },
    getNewPlanPrice() {
      if (this.selectedProduct) {
        return this.selectedProduct?.cost
      }
    },
    subscribeToNewProduct({ resolve, reject }) {
      // gets the apparatus ids of the apparatuses that need to be added to the new plan
      const apparatusIds = this.subscription[0].apparatuses
        .filter(x => x.apparatusId)
        .map(apparatusIds => apparatusIds.apparatusId)
      // create purchase intent
      this.$store
        .dispatch('createPurchaseIntent', {
          organizationId: this.accountData.organizationId,
          productId: this.selectedProduct?.productId,
          apparatusIds: apparatusIds
        })
        .then(response => {
          this.$store
            .dispatch('updateSubscriptionProduct', {
              organizationId: this.accountData?.organizationId,
              productId: this.selectedProduct?.productId
            })
            .then(response => {
              resolve(response)
              this.$bvModal.hide('change-cadence-payment')
              this.pollForSubscriptionDataAndRefresh()
            })
            .catch(error => {
              reject(this.$t('update sub failed'))
            })
        })
        .catch(error => {
          reject(this.$t('xPlanChangesFailed'))
        })
    }
  },
  async mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
      await this.$store
        .dispatch('getSubscriptionDetails', this.accountData.organizationId)
        .then(response => {
          // Find non canceled, non dealer managed subscriptions
          this.subscription = response.data?.subscriptions.filter(
            x => x.status !== Constants.SubscriptionDetailCanceled && x.store !== Constants.DealerSubscriptionStore
          )
          if (this.subscription) {
            this.purchasedDate = this.subscription[0]?.purchasedDate
            this.currentSubPrice = this.subscription[0]?.product?.cost
            this.apparatuses = this.subscription[0]?.apparatuses
            if (this.subscription[0]?.product?.durationInMonths === 12) {
              this.yearly = true
              // Replace yearly with monthly to reflect productId of desired plan
              this.currentSubProductId = this.subscription[0]?.product?.productId.replace('-yearly', '-monthly')
            } else {
              this.monthly = true
              // Replace monthly with yearly to reflect productId of desired plan
              this.currentSubProductId = this.subscription[0]?.product?.productId.replace('-monthly', '-yearly')
            }
          }
        })
        .catch(e => {
          this.$store.dispatch('displayToast', {
            title: this.$t('xSomethingWentWrong'),
            message: this.$t('xWaitAndTryAgainErrorText'),
            variant: 'danger'
          })
        })

      const isCurrentSubFromDifferentStore = isSubscriptionFromNonWebStore(this.subscription)
      // FindStoreProducts throws an error if the store is incorrect
      if (!isCurrentSubFromDifferentStore) {
        await this.$store
          .dispatch('getSubscriptionProductsByStoreOrganization', { organizationId: this.accountData.organizationId })
          .then(response => {
            this.productsArray = response.data?.products
            if (this.yearly) {
              const newProduct = this.productsArray.filter(
                x => x.durationInMonths === 1 && x.productId === this.currentSubProductId
              )
              this.selectedProduct = newProduct[0]
            } else if (this.monthly) {
              const newProduct = this.productsArray.filter(
                x => x.durationInMonths === 12 && x.productId === this.currentSubProductId
              )
              this.selectedProduct = newProduct[0]
            }
          })
          .catch(e => {
            this.$store.dispatch('displayToast', {
              title: this.$t('xSomethingWentWrong'),
              message: this.$t('xWaitAndTryAgainErrorText'),
              variant: 'danger'
            })
          })
      }
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  }
}
</script>
<!-- Had to overwrite the size of the modal to match what was shown in figma -->
<style lang="scss">
#payment-cadence-modal {
  .modal-size {
    max-width: 600px !important;
  }
}
</style>
